import React, { useState, useEffect } from 'react';
import { 
  CreditCard, 
  Eye, 
  Copy, 
  Package, 
  X,
  CheckCircle,
  Zap,
  Sparkles,
  Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { TransactionService } from '../../services/transactionService';
import { ProductService } from '../../services/productService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';

const normalizeText = (value) => String(value || '').trim();

const resolveProductImageUrl = (product = {}) => {
  return normalizeText(product?.detailImageUrl) ||
    normalizeText(product?.imageUrl) ||
    normalizeText(product?.coverImageUrl) ||
    '';
};

const CardHistory = () => {
  const { currentUser } = useAuth();
  const { addNotification } = useNotification();
  
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [viewedCards, setViewedCards] = useState(new Set());
  const [productImageMap, setProductImageMap] = useState({});
  const [imageLoadErrors, setImageLoadErrors] = useState({});

  useEffect(() => {
    loadData();
    // Charger les cartes vues depuis localStorage
    const savedViewedCards = localStorage.getItem(`viewedCards_${currentUser?.uid}`);
    if (savedViewedCards) {
      setViewedCards(new Set(JSON.parse(savedViewedCards)));
    }
  }, [currentUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Charger les achats (cartes) et les images produits pour les achats legacy
      const [userPurchases, products] = await Promise.all([
        TransactionService.getUserPurchases(currentUser.uid),
        ProductService.getAllProducts()
      ]);
      const nextProductImageMap = products.reduce((result, product) => {
        const imageUrl = resolveProductImageUrl(product);
        if (product?.id && imageUrl) {
          result[product.id] = imageUrl;
        }

        return result;
      }, {});

      setPurchases(userPurchases);
      setProductImageMap(nextProductImageMap);
      
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      addNotification('error', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
    return `${formatted} DA`;
  };

  const formatDate = (date) => {
    if (!date) return 'Date inconnue';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour déterminer si une carte est nouvelle (moins de 24h et pas encore vue)
  const isNewCard = (createdAt, cardId) => {
    if (!createdAt) return false;
    // Si la carte a déjà été vue, elle n'est plus "nouvelle"
    if (viewedCards.has(cardId)) return false;
    
    const cardDate = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const now = new Date();
    const diffInHours = (now - cardDate) / (1000 * 60 * 60);
    return diffInHours < 24;
  };

  // Fonction pour déterminer si une carte est récente (moins de 7 jours et pas encore vue)
  const isRecentCard = (createdAt, cardId) => {
    if (!createdAt) return false;
    // Si la carte a déjà été vue, elle n'est plus "récente"
    if (viewedCards.has(cardId)) return false;
    
    const cardDate = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const now = new Date();
    const diffInDays = (now - cardDate) / (1000 * 60 * 60 * 24);
    return diffInDays < 7 && diffInDays >= 1;
  };

  const handleViewCard = (purchase) => {
    setSelectedCard(purchase);
    setShowCardModal(true);
    
    // Marquer la carte comme vue
    const newViewedCards = new Set(viewedCards);
    newViewedCards.add(purchase.id);
    setViewedCards(newViewedCards);
    
    // Sauvegarder dans localStorage
    localStorage.setItem(`viewedCards_${currentUser.uid}`, JSON.stringify([...newViewedCards]));
  };

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code);
    addNotification('success', 'Code copié dans le presse-papiers');
  };

  const handleRecharge = (productName) => {
    if (isPubg(productName)) {
      window.open('https://www.midasbuy.com/midasbuy/dz/redeem/pubgm', '_blank', 'noopener,noreferrer');
    } else if (isFreeFire(productName)) {
      window.open('https://shop2game.com/', '_blank', 'noopener,noreferrer');
    }
  };

  const isFreeFire = (productName) => {
    return productName && productName.toLowerCase().includes('free fire');
  };

  const isPubg = (productName) => {
    return productName && (productName.toLowerCase().includes('pubg') || productName.toLowerCase().includes('battlegrounds'));
  };

  const shouldShowRechargeButton = (productName) => {
    return isFreeFire(productName) || isPubg(productName);
  };

  const getPurchaseImageUrl = (purchase = {}) => {
    return normalizeText(purchase?.productImageUrl) ||
      normalizeText(productImageMap[purchase?.productId]) ||
      '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
          Mes Cartes
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Consultez vos cartes achetées et leurs codes
        </p>
      </div>

      {/* Cards */}
      <div className="bg-white rounded-lg shadow-sm p-3 sm:p-6">
        {purchases.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {purchases.map((purchase) => {
              const isNew = isNewCard(purchase.createdAt, purchase.id);
              const isRecent = isRecentCard(purchase.createdAt, purchase.id);
              const productImageUrl = getPurchaseImageUrl(purchase);
              
              return (
                <div 
                  key={purchase.id} 
                  className={`relative rounded-lg p-4 border transition-all duration-200 hover:shadow-md ${
                    isNew 
                      ? 'bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-300 shadow-emerald-100 shadow-lg' 
                      : isRecent 
                        ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200' 
                        : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
                  }`}
                >
                  {/* Badge nouveau/récent */}
                  {isNew && (
                    <div className="absolute -top-2 -right-2 z-10">
                      <div className="bg-emerald-500 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center shadow-lg animate-pulse">
                        <Sparkles className="w-3 h-3 mr-1" />
                        NOUVEAU
                      </div>
                    </div>
                  )}
                  {isRecent && !isNew && (
                    <div className="absolute -top-2 -right-2 z-10">
                      <div className="bg-blue-500 text-white text-xs font-medium px-2 py-1 rounded-full flex items-center shadow-md">
                        <Clock className="w-3 h-3 mr-1" />
                        RÉCENT
                      </div>
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center flex-1 min-w-0">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 overflow-hidden rounded-lg flex items-center justify-center flex-shrink-0 ${
                        isNew 
                          ? 'bg-emerald-600' 
                          : isRecent 
                            ? 'bg-blue-600' 
                            : 'bg-gray-600'
                      }`}>
                        {productImageUrl && !imageLoadErrors[purchase.id] ? (
                          <img
                            src={productImageUrl}
                            alt={purchase.productName}
                            className="h-full w-full object-cover"
                            onError={() => setImageLoadErrors((current) => ({ ...current, [purchase.id]: true }))}
                          />
                        ) : (
                          <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                        )}
                      </div>
                      <div className="ml-2 sm:ml-3 min-w-0 flex-1">
                        <h3 className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                          {purchase.productName}
                        </h3>
                        <p className="text-xs text-gray-600 truncate">
                          {purchase.offerName}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                      isNew 
                        ? 'bg-emerald-200 text-emerald-800' 
                        : 'bg-success-100 text-success-800'
                    }`}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Acheté</span>
                      <span className="sm:hidden">✓</span>
                    </span>
                  </div>
                  
                  <div className="space-y-1 sm:space-y-2 mb-3">
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Qté:</span>
                      <span className="font-medium text-gray-900">{purchase.quantity}</span>
                    </div>
                    <div className="flex justify-between text-xs sm:text-sm">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(purchase.totalCost)}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-gray-600">Date:</span>
                      <span className="font-medium text-gray-900 text-right">
                        {formatDate(purchase.createdAt)}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleViewCard(purchase)}
                    className={`w-full flex items-center justify-center px-3 py-2 text-white rounded-lg transition-colors text-xs sm:text-sm font-medium ${
                      isNew 
                        ? 'bg-emerald-600 hover:bg-emerald-700' 
                        : isRecent 
                          ? 'bg-blue-600 hover:bg-blue-700' 
                          : 'bg-gray-600 hover:bg-gray-700'
                    }`}
                  >
                    <Eye className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Voir les codes</span>
                    <span className="sm:hidden">Codes</span>
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 sm:py-12">
            <CreditCard className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Aucune carte achetée</h3>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6">Vous n'avez pas encore acheté de cartes.</p>
            <button
              onClick={() => window.location.href = '/catalog'}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base"
            >
              <Package className="w-4 h-4 mr-2" />
              Voir le catalogue
            </button>
          </div>
        )}
      </div>

      {/* Card Modal - Optimisé pour mobile */}
      {showCardModal && selectedCard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex min-w-0 items-center gap-3 pr-4">
                  <div className="h-10 w-10 shrink-0 overflow-hidden rounded-xl bg-gray-100">
                    {getPurchaseImageUrl(selectedCard) && !imageLoadErrors[`modal-${selectedCard.id}`] ? (
                      <img
                        src={getPurchaseImageUrl(selectedCard)}
                        alt={selectedCard.productName}
                        className="h-full w-full object-cover"
                        onError={() => setImageLoadErrors((current) => ({ ...current, [`modal-${selectedCard.id}`]: true }))}
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gray-700">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                    )}
                  </div>
                  <h3 className="min-w-0 text-base sm:text-lg font-semibold text-gray-900 truncate">
                    Codes - {selectedCard.productName}
                  </h3>
                </div>
                <button
                  onClick={() => setShowCardModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                {selectedCard.offerName} • {selectedCard.quantity} code(s) • {formatCurrency(selectedCard.totalCost)}
              </p>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                {selectedCard.codes?.map((codeItem, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900">
                          Code #{index + 1}
                        </p>
                        <p className="text-base sm:text-lg font-mono font-bold text-primary-600 mt-1 break-all">
                          {codeItem.code}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Valeur: {codeItem.value}
                        </p>
                      </div>
                      <div className="flex flex-row sm:flex-col items-stretch gap-2">
                         <button
                           onClick={() => handleCopyCode(codeItem.code)}
                           className="flex items-center justify-center px-3 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm flex-1 sm:flex-none"
                         >
                           <Copy className="w-4 h-4 mr-2" />
                           Copier
                         </button>
                         {shouldShowRechargeButton(selectedCard.productName) && (
                           <button
                             onClick={() => handleRecharge(selectedCard.productName)}
                             className="flex items-center justify-center px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm flex-1 sm:flex-none"
                           >
                             <Zap className="w-4 h-4 mr-2" />
                             Recharger
                           </button>
                         )}
                       </div>
                    </div>
                  </div>
                )) || (
                  <div className="text-center py-6 sm:py-8">
                    <p className="text-gray-500">Aucun code disponible</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs sm:text-sm text-gray-600">
                <span>Achat effectué le {formatDate(selectedCard.createdAt)}</span>
                <span className="font-mono">ID: {selectedCard.id}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardHistory;
